import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { Button, Input, Switch } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import type { CurrencyDto } from '@shared/api/contracts';
import {
  acceptedCurrencies,
  filterCurrencyGroups,
  groupCurrencies,
  isAllCurrenciesSelected,
  isGroupEnabled,
  selectAllCurrencies,
  selectionSummary,
  toggleAsset,
  toggleNetwork,
  type CurrencyAssetGroup,
  type CurrencyListFilter,
} from '@features/projects';

interface ProjectCurrencyPickerProps {
  currencies: CurrencyDto[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  onIssueChange?: (issue: string | null) => void;
  resetKey?: string;
  error?: string;
}

const FILTERS: CurrencyListFilter[] = ['all', 'stable', 'native'];

export function ProjectCurrencyPicker({
  currencies,
  selectedIds,
  onChange,
  onIssueChange,
  resetKey,
  error,
}: ProjectCurrencyPickerProps) {
  const { t } = useTranslation('cabinet');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<CurrencyListFilter>('all');
  const [openWithoutNetworks, setOpenWithoutNetworks] = useState<Set<string>>(() => new Set());

  const groups = useMemo(() => groupCurrencies(acceptedCurrencies(currencies)), [currencies]);
  const visible = useMemo(
    () => filterCurrencyGroups(groups, filter, query),
    [groups, filter, query],
  );
  const summary = selectionSummary(groups, selectedIds, openWithoutNetworks);
  const allSelected = isAllCurrenciesSelected(groups, selectedIds);
  const issue = summary.missing.length
    ? t('projects.form.currenciesNetworkRequired', { code: summary.missing.join(', ') })
    : null;

  useEffect(() => {
    setQuery('');
    setFilter('all');
    setOpenWithoutNetworks(new Set());
  }, [resetKey]);

  useEffect(() => {
    onIssueChange?.(issue);
  }, [issue, onIssueChange]);

  const clearArmed = (code?: string) => {
    setOpenWithoutNetworks((current) => {
      const next = new Set(current);
      if (code) next.delete(code);
      else next.clear();
      return next;
    });
  };

  const handleAssetToggle = (code: string, enabled: boolean) => {
    onChange(toggleAsset(groups, selectedIds, code, enabled));
    clearArmed(code);
  };

  const handleNetworkToggle = (code: string, networkId: number) => {
    const result = toggleNetwork(groups, selectedIds, code, networkId);
    onChange(result.selectedIds);
    setOpenWithoutNetworks((current) => {
      const next = new Set(current);
      if (result.emptied) next.add(code);
      else next.delete(code);
      return next;
    });
  };

  const handleSelectAll = () => {
    onChange(selectAllCurrencies(groups, !allSelected));
    clearArmed();
  };

  let lastKind: CurrencyAssetGroup['kind'] | null = null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-title">{t('projects.form.currenciesTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('projects.form.currenciesMeta', {
              enabled: summary.coins,
              total: summary.total,
              options: summary.options,
            })}
          </p>
        </div>
        {groups.length > 0 ? (
          <Button type="button" variant="link" className="h-tap px-0" onClick={handleSelectAll}>
            {allSelected
              ? t('projects.form.currenciesDisableAll')
              : t('projects.form.currenciesEnableAll')}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('projects.form.currenciesSearch')}
            aria-label={t('projects.form.currenciesSearch')}
            autoComplete="off"
            icon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="inline-flex w-full max-w-full shrink-0 flex-wrap rounded-lg bg-muted p-1 sm:w-auto" role="tablist">
          {FILTERS.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={filter === item}
              className={cn(
                'h-9 flex-1 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors duration-fast ease-standard sm:flex-none',
                filter === item && 'bg-background text-foreground shadow-sm',
              )}
              onClick={() => setFilter(item)}
            >
              {t(`projects.form.currenciesFilter.${item}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {visible.length === 0 ? (
          <p className="col-span-full py-6 text-center text-sm text-muted-foreground">
            {t('projects.form.currenciesEmpty')}
          </p>
        ) : (
          visible.map((group) => {
            const showHeading = filter === 'all' && group.kind !== lastKind;
            lastKind = group.kind;
            const enabled = isGroupEnabled(group, selectedIds, openWithoutNetworks);
            const selectedCount = group.options.filter((option) => selectedIds.includes(option.id)).length;
            const missingNetwork = group.kind === 'stable' && openWithoutNetworks.has(group.code);

            return (
              <div key={group.code} className="contents">
                {showHeading ? (
                  <p className="col-span-full pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(
                      group.kind === 'stable'
                        ? 'projects.form.currenciesGroupStable'
                        : 'projects.form.currenciesGroupNative',
                    )}
                  </p>
                ) : null}
                <article
                  className={cn(
                    'flex flex-col gap-3 rounded-2xl border border-border p-4',
                    !enabled && 'bg-muted/40',
                    missingNetwork && 'border-destructive',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <AssetMark code={group.code} icon={group.icon} muted={!enabled} />
                    <div className={cn('min-w-0 flex-1', !enabled && 'opacity-60')}>
                      <p className="truncate text-sm font-semibold">
                        {group.code}
                        {group.privacy ? (
                          <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 align-middle text-[11px] font-semibold text-amber-800">
                            {t('projects.form.currenciesPrivacy')}
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {group.name}
                        {group.kind === 'stable' && enabled
                          ? ` · ${t('projects.form.currenciesNetworksCount', {
                              selected: selectedCount,
                              total: group.options.length,
                            })}`
                          : ''}
                      </p>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(checked) => handleAssetToggle(group.code, checked)}
                      aria-label={t('projects.form.currenciesAccept', { code: group.code })}
                    />
                  </div>

                  {group.kind === 'stable' ? (
                    <div className={cn('flex flex-wrap gap-1.5', !enabled && 'opacity-45')}>
                      {group.options.map((option) => {
                        const pressed = selectedIds.includes(option.id);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={pressed}
                            title={t('projects.form.currenciesNetworkTitle', {
                              name: option.fullName,
                              fee: t(
                                option.fee === 'high'
                                  ? 'projects.form.currenciesFeeHigh'
                                  : 'projects.form.currenciesFeeLow',
                              ),
                            })}
                            className={cn(
                              'inline-flex h-tap items-center gap-1.5 rounded-md border px-2.5 text-sm font-medium shadow-sm transition-colors duration-fast ease-standard sm:h-8',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              pressed
                                ? 'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
                                : 'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                            )}
                            onClick={() => handleNetworkToggle(group.code, option.id)}
                          >
                            {pressed ? <Check className="size-3.5" /> : null}
                            {option.icon ? (
                              <img src={option.icon} alt="" className="size-4 rounded-full" />
                            ) : (
                              <span
                                className={cn(
                                  'size-1.5 rounded-full',
                                  option.fee === 'high' ? 'bg-amber-500' : 'bg-emerald-500',
                                )}
                              />
                            )}
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={cn('text-sm text-muted-foreground', !enabled && 'opacity-60')}>
                      {t('projects.form.currenciesNativeNetwork', { network: group.nativeNetworkName })}
                    </p>
                  )}

                  {missingNetwork ? (
                    <p className="text-xs font-medium text-destructive">
                      {t('projects.form.currenciesNetworkRequired', { code: group.code })}
                    </p>
                  ) : null}
                </article>
              </div>
            );
          })
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <i className="size-1.5 rounded-full bg-emerald-500" />
          {t('projects.form.currenciesLegendLow')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="size-1.5 rounded-full bg-amber-500" />
          {t('projects.form.currenciesLegendHigh')}
        </span>
        <span>{t('projects.form.currenciesLegendHidden')}</span>
      </div>

      {error && !issue ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function AssetMark({ code, icon, muted }: { code: string; icon?: string; muted: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-[10px] font-bold',
        muted && 'opacity-50 grayscale',
      )}
    >
      {icon ? <img src={icon} alt="" className="size-9 object-cover" /> : code.slice(0, 4)}
    </span>
  );
}
